<script setup lang="ts">
import type { TicketOrderLine } from "@/types";

withDefaults(
  defineProps<{
    lines: TicketOrderLine[];
    totalPrice: number;
    discountAmount?: number;
    formatPrice: (value: number) => string;
    actionLabel: string;
    loadingLabel?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    compact?: boolean;
  }>(),
  {
    loadingLabel: "",
    isLoading: false,
    isDisabled: false,
    compact: false,
    discountAmount: 0,
  },
);

const emit = defineEmits<{
  action: [];
}>();
</script>

<template>
  <aside class="rounded-[40px] bg-white p-6 shadow-widget">
    <h2 class="m-0 font-semibold leading-tight" :class="'text-[32px]'">
      Ваш заказ
    </h2>

    <div class="mt-6 grid gap-4">
      <div
        v-for="line in lines"
        :key="line.ticket.id"
        class="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2"
      >
        <span
          class="min-w-0 overflow-hidden text-lg font-normal leading-[22px] [overflow-wrap:anywhere] after:ml-1.5 after:mr-[-100%] after:inline-block after:w-full after:border-b after:border-dashed after:border-ink/20 after:content-['']"
        >
          {{ line.ticket.title }}
          <span class="whitespace-nowrap">{{ line.quantity }} шт.</span>
        </span>
        <span
          class="whitespace-nowrap text-lg font-semibold text-ink leading-[22px]"
        >
          {{ formatPrice(line.amount) }}
        </span>
      </div>
      <p
        v-if="!lines.length"
        class="text-lg leading-[22px] font-medium text-ink/80"
      >
        Выберите нужные тарифы для заказа
      </p>
    </div>

    <div class="my-6 h-px bg-ink/10" />

    <div
      v-if="discountAmount > 0"
      class="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2 text-darkred"
    >
      <span
        class="min-w-0 overflow-hidden text-lg font-normal leading-[22px] [overflow-wrap:anywhere] after:ml-1.5 after:mr-[-100%] after:inline-block after:w-full after:border-b after:border-dashed after:border-ink/20 after:content-['']"
      >
        Скидка по промокоду
      </span>
      <span
        class="whitespace-nowrap text-lg font-semibold leading-[22px] text-darkred"
      >
        {{ formatPrice(discountAmount) }}
      </span>
    </div>

    <div v-if="discountAmount > 0" class="my-6 h-px bg-ink/10" />

    <div class="flex items-center justify-between">
      <span class="font-semibold text-2xl">Итого</span>
      <span class="font-semibold text-brand text-4xl">
        {{ formatPrice(totalPrice) }}
      </span>
    </div>

    <button
      class="h-[60px] mt-6 w-full cursor-pointer rounded-full bg-brand px-8 font-semibold text-white transition hover:bg-brand/90 disabled:cursor-default disabled:bg-brand/35 text-2xl leading-7"
      type="button"
      :disabled="isDisabled || isLoading"
      @click="emit('action')"
    >
      {{ isLoading && loadingLabel ? loadingLabel : actionLabel }}
    </button>
  </aside>
</template>
